// Compound interest projections + investment options catalog

export function projectValue({ initial = 0, monthly = 0, years = 5, annualRate = 0.03 }) {
  // Monthly compounding
  const r = annualRate / 12;
  const n = years * 12;
  const fvInitial = initial * Math.pow(1 + r, n);
  const fvMonthly = r === 0 ? monthly * n : monthly * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
  return Math.round(fvInitial + fvMonthly);
}

export function buildGrowthSeries({ initial, monthly, years, annualRate }) {
  const series = [];
  for (let y = 0; y <= years; y += 1) {
    series.push({
      year: y,
      value: projectValue({ initial, monthly, years: y, annualRate }),
    });
  }
  return series;
}

// Investment options
// Each option carries a plain-language description, why it fits a profile,
// a concrete historical example, and an assumed annual rate.
export const OPTIONS = {
  livret_a: {
    id: "livret_a",
    country: "FR",
    rate: 0.03,
    tags: ["prudent"],
    fr: {
      name: "Livret A",
      short: "Un compte d'épargne garanti par l'État français.",
      why:
        "Le Livret A est idéal si vous commencez et voulez zéro risque. Votre argent reste disponible à tout moment et il n'y a aucun impôt sur les intérêts.",
      example:
        "1 000 € placés en 2019 sur un Livret A rapportent environ 130 € d'intérêts au bout de 5 ans, sans risque de perte.",
      glossary_term: "Livret",
    },
    en: {
      name: "Livret A (State Savings Account)",
      short: "A savings account backed by the French State.",
      why:
        "Livret A is perfect when you start and want zero risk. Your money stays available at all times and interest is tax-free.",
      example:
        "€1,000 saved in 2019 in a Livret A earned around €130 in interest over 5 years, with no risk of loss.",
      glossary_term: "Savings account",
    },
  },
  high_yield_savings: {
    id: "high_yield_savings",
    country: "INTL",
    rate: 0.035,
    tags: ["prudent"],
    fr: {
      name: "Compte d'épargne à intérêt élevé",
      short: "Un compte bancaire sécurisé qui rapporte un petit intérêt fixe.",
      why:
        "C'est l'option la plus simple si vous n'avez jamais épargné. Aucun risque de perdre votre argent, et vous pouvez le retirer quand vous voulez.",
      example:
        "1 000 $ placés il y a 5 ans sur un compte à 3 % par an rapportent environ 160 $ d'intérêts, votre capital reste protégé.",
      glossary_term: "Compte d'épargne",
    },
    en: {
      name: "High-yield savings account",
      short: "A safe bank account that pays a small, steady interest.",
      why:
        "It's the simplest option if you've never saved before. No risk of losing your money, and you can withdraw anytime.",
      example:
        "$1,000 placed 5 years ago in a 3% savings account earned around $160 in interest, with your capital protected.",
      glossary_term: "Savings account",
    },
  },
  assurance_vie: {
    id: "assurance_vie",
    country: "FR",
    rate: 0.045,
    tags: ["equilibre"],
    fr: {
      name: "Assurance-vie (fonds euros)",
      short: "Un placement long terme, à capital garanti sur le fonds euros.",
      why:
        "L'assurance-vie en fonds euros rapporte un peu plus que le Livret A, tout en protégeant votre capital. C'est un excellent choix pour préparer une retraite ou un projet dans 5 à 10 ans.",
      example:
        "10 000 € placés en 2015 sur une assurance-vie fonds euros valent environ 12 500 € aujourd'hui, sans avoir perdu un centime.",
      glossary_term: "Assurance-vie",
    },
    en: {
      name: "Life insurance (euro fund)",
      short: "A long-term plan with guaranteed capital on the euro fund.",
      why:
        "Life insurance in a euro fund earns a bit more than Livret A while protecting your capital. Great for preparing retirement or a project in 5–10 years.",
      example:
        "€10,000 placed in 2015 in a euro-fund life insurance is worth around €12,500 today, without losing a cent.",
      glossary_term: "Life insurance",
    },
  },
  balanced_fund: {
    id: "balanced_fund",
    country: "INTL",
    rate: 0.05,
    tags: ["equilibre"],
    fr: {
      name: "Fonds équilibré (obligations + actions)",
      short: "Un mélange sûr et modéré, géré par une banque ou un assureur.",
      why:
        "Un fonds équilibré combine des obligations (stables) et des actions (dynamiques). Il rapporte davantage qu'un livret sur le long terme, tout en limitant les mauvaises surprises.",
      example:
        "1 000 $ placés en 2015 dans un fonds équilibré valent environ 1 600 $ dix ans plus tard, malgré quelques baisses en chemin.",
      glossary_term: "Fonds équilibré",
    },
    en: {
      name: "Balanced fund (bonds + stocks)",
      short: "A modest and safe mix, managed by a bank or insurer.",
      why:
        "A balanced fund mixes bonds (stable) and stocks (growth). It earns more than a savings account over the long run, while limiting bad surprises.",
      example:
        "$1,000 placed in a balanced fund in 2015 is worth around $1,600 ten years later, despite some dips along the way.",
      glossary_term: "Balanced fund",
    },
  },
  etf_world: {
    id: "etf_world",
    country: "ALL",
    rate: 0.07,
    tags: ["dynamique"],
    fr: {
      name: "ETF Monde (MSCI World)",
      short: "Un panier d'actions des plus grandes entreprises mondiales.",
      why:
        "Un ETF Monde vous permet d'investir dans plus de 1 500 entreprises d'un coup, avec des frais très bas. Sur 10 ans, il a historiquement rapporté beaucoup plus qu'un livret — mais il peut varier fortement à court terme.",
      example:
        "1 000 € investis en 2014 dans un ETF Monde valent environ 2 400 € en 2024, soit +140 % en 10 ans (avec des hauts et des bas).",
      glossary_term: "ETF",
    },
    en: {
      name: "World ETF (MSCI World)",
      short: "A basket of stocks from the largest companies in the world.",
      why:
        "A World ETF lets you invest in 1,500+ companies at once, with very low fees. Over 10 years it has historically earned much more than a savings account — but it can swing sharply short-term.",
      example:
        "€1,000 invested in 2014 in a World ETF is worth around €2,400 in 2024, i.e. +140% in 10 years (with ups and downs).",
      glossary_term: "ETF",
    },
  },
  // Belgium
  be_regulated_savings: {
    id: "be_regulated_savings",
    country: "BE",
    rate: 0.0175,
    tags: ["prudent"],
    fr: {
      name: "Compte d'épargne réglementé",
      short: "Un compte d'épargne belge protégé par la garantie de l'État (jusqu'à 100 000 €).",
      why: "C'est l'option la plus sûre en Belgique pour commencer. Vos intérêts sont partiellement exonérés d'impôt et votre capital est protégé par l'État.",
      example: "1 000 € placés en 2020 sur un compte d'épargne réglementé rapportent environ 70 € au bout de 5 ans, sans risque.",
      glossary_term: "Compte d'épargne",
    },
    en: {
      name: "Belgian regulated savings account",
      short: "A Belgian savings account protected by the State (up to €100,000).",
      why: "It's the safest option to start in Belgium. Your interest is partly tax-exempt and your capital is protected by the State.",
      example: "€1,000 placed in 2020 in a regulated savings account earns around €70 over 5 years, with no risk.",
      glossary_term: "Savings account",
    },
  },
  be_branche21: {
    id: "be_branche21",
    country: "BE",
    rate: 0.025,
    tags: ["equilibre"],
    fr: {
      name: "Assurance-vie Branche 21",
      short: "Un placement long terme belge à capital garanti.",
      why: "La Branche 21 garantit votre capital et offre un rendement stable, parfait pour préparer sa retraite sans stress.",
      example: "10 000 € placés en 2015 en Branche 21 valent environ 11 300 € aujourd'hui, garantis.",
      glossary_term: "Assurance-vie",
    },
    en: {
      name: "Belgian Branch 21 life insurance",
      short: "A long-term Belgian investment with guaranteed capital.",
      why: "Branch 21 guarantees your capital and offers a stable return, perfect for preparing retirement stress-free.",
      example: "€10,000 placed in 2015 in Branch 21 is worth around €11,300 today, guaranteed.",
      glossary_term: "Life insurance",
    },
  },
  // Switzerland
  ch_savings: {
    id: "ch_savings",
    country: "CH",
    rate: 0.01,
    tags: ["prudent"],
    fr: {
      name: "Compte d'épargne (Suisse)",
      short: "Un compte bancaire suisse sécurisé, argent disponible à tout moment.",
      why: "Idéal pour démarrer : aucun risque, argent disponible, et vos dépôts sont protégés jusqu'à 100 000 CHF par banque.",
      example: "1 000 CHF placés en 2019 rapportent environ 40 CHF d'intérêts en 5 ans, capital intact.",
      glossary_term: "Compte d'épargne",
    },
    en: {
      name: "Swiss savings account",
      short: "A safe Swiss bank account, money available at any time.",
      why: "Perfect to start: no risk, money accessible, deposits protected up to CHF 100,000 per bank.",
      example: "CHF 1,000 saved in 2019 earned around CHF 40 in interest over 5 years, capital intact.",
      glossary_term: "Savings account",
    },
  },
  ch_3a: {
    id: "ch_3a",
    country: "CH",
    rate: 0.03,
    tags: ["equilibre"],
    fr: {
      name: "3e pilier A (prévoyance)",
      short: "Un placement prévoyance suisse avec avantage fiscal important.",
      why: "Le 3e pilier A vous fait économiser jusqu'à 30 % d'impôts par an tout en préparant votre retraite. Fortement recommandé si vous travaillez en Suisse.",
      example: "7 000 CHF versés en 2015 dans un 3e pilier A valent environ 9 100 CHF aujourd'hui, avec des économies d'impôt en plus.",
      glossary_term: "3e pilier",
    },
    en: {
      name: "3rd Pillar A (Swiss pension)",
      short: "A Swiss pension investment with major tax advantages.",
      why: "Pillar 3a saves you up to 30% in taxes yearly while preparing your retirement. Highly recommended if you work in Switzerland.",
      example: "CHF 7,000 placed in 2015 in a Pillar 3a is worth around CHF 9,100 today, plus tax savings.",
      glossary_term: "3rd Pillar",
    },
  },
  // Canada
  ca_tfsa_savings: {
    id: "ca_tfsa_savings",
    country: "CA",
    rate: 0.035,
    tags: ["prudent"],
    fr: {
      name: "CELI compte d'épargne",
      short: "Un Compte d'Épargne Libre d'Impôt canadien, sécurisé et sans impôt sur les gains.",
      why: "Le CELI est parfait pour commencer au Canada : vos intérêts ne sont jamais imposés, votre argent reste accessible et le capital est garanti.",
      example: "1 000 $ placés en 2019 dans un CELI à 3,5 % rapportent environ 190 $ en 5 ans, entièrement libres d'impôt.",
      glossary_term: "CELI",
    },
    en: {
      name: "TFSA savings account",
      short: "A Canadian Tax-Free Savings Account, safe and tax-exempt on gains.",
      why: "The TFSA is perfect to start in Canada: interest is never taxed, your money stays accessible, and capital is guaranteed.",
      example: "$1,000 placed in 2019 in a 3.5% TFSA earned around $190 over 5 years, entirely tax-free.",
      glossary_term: "TFSA",
    },
  },
  ca_rrsp_balanced: {
    id: "ca_rrsp_balanced",
    country: "CA",
    rate: 0.05,
    tags: ["equilibre"],
    fr: {
      name: "REER équilibré",
      short: "Un Régime Enregistré d'Épargne-Retraite avec fonds équilibré (Canada).",
      why: "Le REER équilibré combine obligations et actions, avec une déduction fiscale immédiate sur vos cotisations. Parfait pour préparer sa retraite au Canada.",
      example: "10 000 $ placés en 2015 dans un REER équilibré valent environ 15 000 $ aujourd'hui, avec en plus une économie d'impôt à chaque versement.",
      glossary_term: "REER",
    },
    en: {
      name: "Balanced RRSP",
      short: "A Registered Retirement Savings Plan with a balanced fund (Canada).",
      why: "The balanced RRSP mixes bonds and stocks, with immediate tax deduction on contributions. Ideal to prepare retirement in Canada.",
      example: "$10,000 placed in 2015 in a balanced RRSP is worth around $15,000 today, plus tax savings at each contribution.",
      glossary_term: "RRSP",
    },
  },
};

export function recommendFor({ risk_profile, country }) {
  if (risk_profile === "dynamique") return OPTIONS.etf_world;
  if (risk_profile === "equilibre") {
    if (country === "FR") return OPTIONS.assurance_vie;
    if (country === "BE") return OPTIONS.be_branche21;
    if (country === "CH") return OPTIONS.ch_3a;
    if (country === "CA") return OPTIONS.ca_rrsp_balanced;
    return OPTIONS.balanced_fund;
  }
  // prudent
  if (country === "FR") return OPTIONS.livret_a;
  if (country === "BE") return OPTIONS.be_regulated_savings;
  if (country === "CH") return OPTIONS.ch_savings;
  if (country === "CA") return OPTIONS.ca_tfsa_savings;
  return OPTIONS.high_yield_savings;
}

export const GLOSSARY = {
  fr: [
    {
      term: "Livret",
      def: "Un compte bancaire spécial pour mettre de l'argent de côté. Votre argent reste toujours accessible et sans risque.",
    },
    {
      term: "Intérêt",
      def: "L'argent que la banque vous donne en remerciement pour avoir laissé votre argent chez elle.",
    },
    {
      term: "Assurance-vie",
      def: "Un placement long terme, souvent proposé par une banque ou un assureur. Vous mettez de l'argent dedans et il grandit chaque année.",
    },
    {
      term: "ETF",
      def: "Un panier qui contient un petit morceau de centaines d'entreprises en même temps. En achetant un ETF, vous investissez dans plein d'entreprises d'un coup.",
    },
    {
      term: "Action",
      def: "Une petite part d'une entreprise. Si l'entreprise va bien, votre action peut prendre de la valeur. Si elle va mal, elle peut en perdre.",
    },
    {
      term: "Obligation",
      def: "Vous prêtez de l'argent à un État ou à une entreprise. En échange, ils vous versent des intérêts chaque année.",
    },
    {
      term: "Rendement",
      def: "C'est le pourcentage que rapporte votre placement chaque année. Par exemple, 3 % veut dire que 100 € deviennent 103 € au bout d'un an.",
    },
    {
      term: "Capital garanti",
      def: "L'argent que vous mettez ne peut pas être perdu. Vous récupérez au minimum ce que vous avez mis.",
    },
    {
      term: "Diversification",
      def: "Ne pas mettre tous ses œufs dans le même panier. On répartit son argent sur plusieurs placements pour réduire les risques.",
    },
    {
      term: "CELI",
      def: "Compte d'Épargne Libre d'Impôt (Canada). Vous mettez de l'argent dedans, il grandit, et vous ne payez aucun impôt sur les gains.",
    },
    {
      term: "REER",
      def: "Régime Enregistré d'Épargne-Retraite (Canada). Chaque euro versé réduit vos impôts de l'année, et l'argent grandit à l'abri.",
    },
    {
      term: "3e pilier",
      def: "Compte d'épargne retraite suisse. Vous économisez pour la retraite tout en payant moins d'impôts chaque année.",
    },
    {
      term: "PEA (Plan d'Épargne en Actions)",
      def: "Un compte spécial pour investir en Bourse européenne. Après 5 ans, vous ne payez presque plus d'impôts sur les gains. Idéal pour la retraite.",
    },
    {
      term: "Dividende",
      def: "Une petite part des bénéfices qu'une entreprise verse à ses actionnaires. Comme un loyer que vous toucheriez pour posséder une action.",
    },
    {
      term: "Inflation",
      def: "La hausse générale des prix. Avec 3 % d'inflation, un pain à 1 € coûtera 1,03 € dans un an. C'est pour ça qu'il faut faire fructifier son épargne : sinon, elle perd du pouvoir d'achat.",
    },
    {
      term: "Fonds en euros",
      def: "La partie garantie d'une assurance-vie. Votre argent est protégé et rapporte 2 à 3 % par an, sans risque.",
    },
    {
      term: "Unités de compte (UC)",
      def: "La partie « investie » d'une assurance-vie, placée en Bourse. Peut rapporter plus que le fonds euros, mais peut aussi baisser à court terme.",
    },
    {
      term: "Frais de gestion",
      def: "Le pourcentage prélevé chaque année par la banque ou l'assureur pour gérer votre argent. Plus c'est bas, plus il vous en reste. À surveiller de près !",
    },
    {
      term: "Horizon de placement",
      def: "La durée pendant laquelle vous laissez votre argent tranquille. Plus c'est long, plus vous pouvez viser un rendement élevé sans stress.",
    },
    {
      term: "Versement programmé",
      def: "Une somme fixe (par exemple 50 €) prélevée automatiquement chaque mois pour investir. La méthode la plus simple et la plus efficace pour débuter.",
    },
    {
      term: "Livret A",
      def: "Le livret d'épargne le plus connu en France. Argent garanti par l'État, disponible à tout moment, sans impôt. Idéal pour l'épargne de précaution.",
    },
  ],
  en: [
    {
      term: "Savings account",
      def: "A special bank account to set money aside. Your money stays accessible and safe.",
    },
    {
      term: "Interest",
      def: "The money the bank gives you as a thank-you for keeping your money with them.",
    },
    {
      term: "Life insurance (financial product)",
      def: "A long-term investment, often offered by a bank or insurer. You put money in and it grows each year.",
    },
    {
      term: "ETF",
      def: "A basket that contains a small piece of hundreds of companies at once. Buying an ETF means investing in many companies at once.",
    },
    {
      term: "Stock",
      def: "A tiny share of a company. If the company does well, your stock can gain value. If it does badly, it can lose value.",
    },
    {
      term: "Bond",
      def: "You lend money to a government or a company. In return, they pay you interest every year.",
    },
    {
      term: "Return",
      def: "The percentage your investment earns each year. For example, 3% means €100 becomes €103 after one year.",
    },
    {
      term: "Guaranteed capital",
      def: "The money you put in cannot be lost. You get back at least what you put in.",
    },
    {
      term: "Diversification",
      def: "Don't put all your eggs in one basket. Spread your money across several investments to reduce risk.",
    },
    {
      term: "TFSA",
      def: "Tax-Free Savings Account (Canada). You put money in, it grows, and you pay no tax on the gains.",
    },
    {
      term: "RRSP",
      def: "Registered Retirement Savings Plan (Canada). Every dollar put in reduces your taxes this year, and money grows tax-sheltered.",
    },
    {
      term: "3rd Pillar",
      def: "Swiss retirement savings account. You save for retirement while paying less tax each year.",
    },
    {
      term: "PEA (Equity Savings Plan)",
      def: "A special account for investing in European stocks. After 5 years, gains are almost tax-free. Ideal for retirement planning.",
    },
    {
      term: "Dividend",
      def: "A small share of a company's profits paid to shareholders. Like rent you'd earn from owning a piece of the company.",
    },
    {
      term: "Inflation",
      def: "The general rise in prices. With 3% inflation, a €1 loaf will cost €1.03 next year. That's why you need your savings to grow: otherwise, they lose purchasing power.",
    },
    {
      term: "Euro fund",
      def: "The guaranteed part of a life insurance policy. Your money is protected and earns 2–3% per year, risk-free.",
    },
    {
      term: "Unit-linked (UC)",
      def: "The \"invested\" part of a life insurance policy, placed in stocks. Can earn more than the euro fund, but can also drop short-term.",
    },
    {
      term: "Management fees",
      def: "The percentage the bank or insurer takes each year to manage your money. The lower, the more you keep. Watch closely!",
    },
    {
      term: "Investment horizon",
      def: "How long you leave your money invested. The longer it is, the more you can aim for higher returns stress-free.",
    },
    {
      term: "Scheduled contribution",
      def: "A fixed amount (e.g. €50) automatically withdrawn every month to invest. The simplest and most effective method to start.",
    },
    {
      term: "Livret A",
      def: "The most well-known savings account in France. Guaranteed by the State, always available, tax-free. Ideal for emergency savings.",
    },
  ],
};

export function formatMoney(value, currency = "EUR", lang = "fr") {
  try {
    return new Intl.NumberFormat(lang === "fr" ? "fr-FR" : "en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${Math.round(value)} ${currency}`;
  }
}

export function currencyForCountry(country) {
  const map = {
    FR: "EUR", DE: "EUR", ES: "EUR", IT: "EUR", BE: "EUR", PT: "EUR", NL: "EUR",
    US: "USD", CA: "CAD", GB: "GBP", CH: "CHF", JP: "JPY", AU: "AUD",
  };
  return map[country] || "EUR";
}
